/**
 * 系统信息（管理端）
 * ---------------------------------------------------------------------------
 * 系统名 / logo / 说明 / 版本号统一由后端 system_configs（分组 system，is_public=1）
 * 下发 —— 与小程序端「关于」页读的是同一份数据，运营在「系统配置 → 系统信息」
 * 里改一次，三端同时变，管理端不需要重新打包。
 *
 * ⚠️ 登录页在**未登录**状态下也要读它，所以走的是免鉴权的
 *    GET /api/system/config/public（该路径已在 JwtInterceptor 白名单内）。
 * ⚠️ 结果缓存在 localStorage：刷新页面时先用缓存渲染，避免 logo 与系统名
 *    先闪一下旧值再变成配置值。
 * ⚠️ 后端对相对路径不做处理（config/public 直接吐库里的原值），所以本地磁盘
 *    存储时拿到的是 /uploads/xxx，需要这里补域名或交给同域/代理。
 *    判断逻辑只放在这里一处，页面不要各写一遍。
 */
import { getPublicConfigs } from '@/api/system'
import fallbackLogo from '@/assets/logo.png'

/** 兜底：后端配置读不到时用（与项目初始形态一致，不让登录页开天窗） */
export const FALLBACK_SYSTEM_INFO = Object.freeze({
  name: '九九次心动',
  desc: '校园纸条盲盒 · 运营后台',
  logo: '',
  version: ''
})

const CACHE_KEY = 'systemInfo'

/** 同步读取缓存（供 store 初始化、首屏渲染用） */
export function readCachedSystemInfo() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    return parsed && parsed.name ? parsed : null
  } catch (e) {
    return null
  }
}

function writeCache(info) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(info))
  } catch (e) {
    // 隐私模式下 localStorage 可能不可写，忽略即可，不影响本次会话渲染
  }
}

/**
 * 从配置列表里挑出 system.* 组装成系统信息
 * @param {Array} list /api/system/config/public 返回的配置数组
 */
export function pickSystemInfo(list) {
  const map = {}
  ;(list || []).forEach(item => {
    if (item && item.configKey) {
      map[item.configKey] = item.configValue || ''
    }
  })

  return {
    name: map['system.name'] || FALLBACK_SYSTEM_INFO.name,
    desc: map['system.desc'] || FALLBACK_SYSTEM_INFO.desc,
    logo: map['system.logo'] || '',
    version: map['system.version'] || ''
  }
}

/**
 * 拉取系统信息。永远不会 reject —— 失败时也返回兜底值，
 * 因为登录页不该因为一个配置接口挂了就渲染不出来。
 * 成功后顺手把浏览器标签页图标也换成配置里的 logo。
 * @returns {Promise<{name:string,desc:string,logo:string,version:string}>}
 */
export function loadSystemInfo() {
  return getPublicConfigs()
    .then(res => {
      const info = pickSystemInfo(res && res.data)
      writeCache(info)
      applyFavicon(info.logo)
      return info
    })
    .catch(() => readCachedSystemInfo() || { ...FALLBACK_SYSTEM_INFO })
}

/**
 * 标签页图标跟随 system.logo
 * public/index.html 里的 favicon 是本地兜底的，这里按配置覆盖。
 */
export function applyFavicon(logo) {
  if (!logo || typeof document === 'undefined') {
    return
  }
  let link = document.querySelector('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'icon')
    document.head.appendChild(link)
  }
  link.setAttribute('href', logo)
}

/**
 * 图片地址 → 可直接用于 <img src> 的地址
 * 空值回退内置 logo；相对路径（/uploads/xxx）在 dev 走 proxy、生产同域，原样使用即可。
 */
export function resolveLogo(logo) {
  if (!logo) {
    return fallbackLogo
  }
  return logo
}

/** 折叠态侧边栏只放一个字，取系统名首字 */
export function shortName(name) {
  const value = (name || '').trim()
  return value ? value.charAt(0) : '心'
}

/**
 * 标签页标题 =「页面名 - 系统名后台管理」
 * ⛔ 之前路由守卫、登录页、布局三处各写了一遍「九九次心动后台管理」，
 *    配置里的 system.name 改了也盖不住 —— 统一走这里。
 * @param {string} pageTitle 页面名（通常取 route.meta.title），空则只显示系统名
 * @param {string} systemName 配置里的系统名
 */
export function formatPageTitle(pageTitle, systemName) {
  const name = systemName || FALLBACK_SYSTEM_INFO.name
  return pageTitle ? `${pageTitle} - ${name}后台管理` : `${name}后台管理`
}
